import React from 'react';
import {
  DollarSign,
  AlertTriangle,
  BarChart3,
  Users,
  Building2,
  Calendar,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import {
  formatMoney,
  formatPercent,
  formatMonthLabel,
  occupancyBarColor,
  resolveOccupancy,
  formatLeasedOccupancy,
  formatOccupancyContext,
  SourceOfTruthBanner,
  PortalUnavailableBanner,
} from './shared';

/**
 * Overview tab — a one-glance "is dormitory housing healthy?" dashboard for
 * HR/admin users.
 *
 * Composition (priority order):
 *   1. Four KPI cards. The headline is "are we paying for empty beds?" —
 *      so vacant-bed cost in dollars (NOT just occupancy %) is a top card.
 *   2. Cost-composition stacked bar — the "money story" in one image:
 *      what fraction of bed-inventory cost lands on occupied vs vacant beds.
 *   3. Per-dorm table sorted by cost descending — drill-down for "which
 *      dorm is the biggest line item, and how full is it?".
 *
 * All metrics are sourced live from the Dormitory Portal labour-cost API.
 * The HR Portal is read-only here.
 */
const OverviewTab = ({ portalOverview, loading, onRefresh }) => {
  // Loading skeleton — only on first load (when there's no data yet).
  // Subsequent refreshes keep the existing data visible with a spinner on
  // the refresh button so the page doesn't flash.
  if (loading && !portalOverview) {
    return <OverviewSkeleton />;
  }

  const summary = portalOverview?.summary || {};
  const costSplit = portalOverview?.cost_split || {};
  const dorms = portalOverview?.dorms || [];
  const monthLabel = portalOverview?.month_label;
  const monthDisplay = formatMonthLabel(monthLabel);
  const dormPortalUrl = portalOverview?.dorm_portal_url;
  const isAvailable = portalOverview?.available !== false;
  const portalError = portalOverview?.error;
  const periodPhrase = monthDisplay || 'this month';

  return (
    <div className="space-y-6">
      <SourceOfTruthBanner
        monthLabel={monthLabel}
        dormPortalUrl={dormPortalUrl}
        onRefresh={onRefresh}
        loading={loading}
      />

      {!isAvailable && <PortalUnavailableBanner error={portalError} />}

      {/* Period stripe — every metric on the page is anchored to a single
          payroll month, so the temporal scope must be visually obvious
          before the eye reaches the KPI numbers. */}
      <PeriodHeader monthDisplay={monthDisplay} />

      {/* KPI cards — preferred metrics card pattern from the design system.
          Order is deliberate: Total Cost (the "what are we spending?"),
          Vacant Cost (the "is any of it wasted?" — the actionable number),
          Occupancy Rate (the health signal), Active Residents (the fleet
          context). Total Dormitories drops to subtitle context. */}
      <section className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 xl:gap-6">
        <KpiCard
          icon={DollarSign}
          label="Total Monthly Cost"
          value={formatMoney(summary.total_monthly_cost)}
          sub={
            Number(summary.total_monthly_cost_third_party) > 0
              && Number(summary.total_monthly_cost_third_party) !== Number(summary.total_monthly_cost)
              ? `${formatMoney(summary.total_monthly_cost_third_party)} third-party · ${periodPhrase}`
              : `Prorated for ${periodPhrase}`
          }
        />
        <KpiCard
          icon={AlertTriangle}
          label="Vacant Bed Cost"
          value={formatMoney(summary.company_vacancy_cost)}
          sub={
            Number(summary.company_vacancy_cost) > 0
              ? `Rent paid for empty beds · ${
                  costSplit.is_month_to_date ? 'month-to-date · ' : ''
                }${periodPhrase}`
              : `No vacancy spend in ${
                  costSplit.is_month_to_date ? 'month-to-date · ' : ''
                }${periodPhrase}`
          }
          tone="warning"
        />
        <KpiCard
          icon={BarChart3}
          label="Occupancy Rate"
          value={formatPercent(summary.occupancy_rate_percent)}
          sub={formatOccupancyContext(summary)}
        />
        <KpiCard
          icon={Users}
          label="Active Residents"
          value={(summary.active_assignments ?? 0).toLocaleString()}
          sub={
            summary.total_dormitories
              ? `Across ${summary.total_dormitories} dormitor${summary.total_dormitories === 1 ? 'y' : 'ies'}`
              : `In ${periodPhrase}`
          }
        />
      </section>

      {/* Cost composition — the "money story" stacked bar. Tells HR at a
          glance what fraction of inventory rent is going to empty beds.
          A 2-segment horizontal bar is the right shape for a binary
          proportion (better than a pie/donut for compact rows), and the
          KPI cards above carry the absolute dollars; this card carries
          the *ratio* and the efficiency verdict ("Efficient" / "Has slack")
          which the KPIs alone do not communicate. */}
      <CostCompositionCard
        costSplit={costSplit}
        monthDisplay={monthDisplay}
        dormCount={dorms.length || summary.total_dormitories || 0}
      />

      {/* Per-dorm breakdown — sorted by cost descending so the biggest line
          item is the first row. Uses DS table classes. */}
      <PerDormBreakdown dorms={dorms} monthDisplay={monthDisplay} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Period header — small, restrained chrome that tells HR exactly which
// payroll month the numbers below describe. Sits between the source-of-truth
// banner and the KPI strip so the temporal scope is fixed before any
// metric is read.
// ---------------------------------------------------------------------------
const PeriodHeader = ({ monthDisplay }) => (
  <div className="flex items-center gap-2 px-1">
    <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
    <p className="text-sm text-gray-700">
      Showing dormitory metrics for{' '}
      <span className="font-semibold text-gray-900 tabular-nums">
        {monthDisplay || 'the current month'}
      </span>
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// KPI card — strict DS preferred-metrics-card pattern.
// `bg-white rounded-2xl shadow-sm border p-6` + `w-12 h-12 bg-red-100 rounded-full`
// + `text-2xl font-bold text-gray-900` + `text-sm text-gray-600`.
// `tone="warning"` swaps the icon color to amber to mirror the
// "this is the wasteful spend" framing without breaking the admin theme.
// ---------------------------------------------------------------------------
const KpiCard = ({ icon: Icon, label, value, sub, tone }) => {
  const iconBg = tone === 'warning' ? 'bg-amber-100' : 'bg-red-100';
  const iconColor = tone === 'warning' ? 'text-amber-600' : 'text-red-600';
  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
      {/* Icon stacks on top at the 2-up mobile width so the hero value gets
          the full card width (dollar figures like "$20,209.64" can't fit
          beside the icon at ~150px and must not truncate). Inline from sm. */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 ${iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${iconColor}`} />
        </div>
        <div className="min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{value}</p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">{label}</p>
          {sub && (
            <p className="text-[11px] sm:text-xs text-gray-400 mt-0.5 truncate">{sub}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cost composition stacked bar — fleet-wide inventory rent (all dorms),
// split occupied vs vacant. Distinct from the per-dorm "Prorated cost"
// column below (that one is stay-segment assignment cost).
// ---------------------------------------------------------------------------
const CostCompositionCard = ({ costSplit, monthDisplay, dormCount = 0 }) => {
  const occupied = Number(costSplit.occupied_cost) || 0;
  const vacant = Number(costSplit.vacant_cost) || 0;
  const total = Number(costSplit.total_cost) || 0;
  const occupiedPct = Number(costSplit.occupied_percent) || 0;
  const vacantPct = Number(costSplit.vacant_percent) || 0;
  const isMtd = Boolean(costSplit.is_month_to_date);
  const daysElapsed = Number(costSplit.days_elapsed) || 0;
  const periodPhrase = monthDisplay || 'this month';
  const timeScope = isMtd
    ? (daysElapsed > 0
      ? `month-to-date through day ${daysElapsed} of ${periodPhrase}`
      : `month-to-date for ${periodPhrase}`)
    : periodPhrase;
  const dormScope =
    dormCount > 0
      ? `across all ${dormCount} dormitor${dormCount === 1 ? 'y' : 'ies'}`
      : 'across all dormitories';

  // Empty-data state — keep the card visible but explain it.
  if (total <= 0) {
    return (
      <section className="bg-white rounded-2xl shadow-sm border p-6">
        <header className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Cost composition</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            No bed-inventory cost recorded {dormScope} for {timeScope}.
          </p>
        </header>
      </section>
    );
  }

  const isEfficient = vacantPct < 10;

  return (
    <section className="bg-white rounded-2xl shadow-sm border p-6">
      <header className="flex items-start justify-between gap-2 sm:gap-4 mb-4">
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
            Cost composition
          </h3>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
            Fleet bed-inventory rent {dormScope} — how {formatMoney(total)}{' '}
            splits between occupied and vacant beds for {timeScope}.
          </p>
        </div>
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
            isEfficient
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-amber-50 text-amber-700'
          }`}
        >
          {isEfficient ? (
            <TrendingUp className="w-3.5 h-3.5" />
          ) : (
            <TrendingDown className="w-3.5 h-3.5" />
          )}
          {isEfficient ? 'Efficient' : 'Has slack'}
        </div>
      </header>

      {/* flex-none + flex-basis — not width% — so labels inside the segments
          cannot shrink the bar away from the true cost split. */}
      <div className="w-full h-8 bg-gray-100 rounded-lg overflow-hidden flex">
        <div
          className="bg-emerald-500 h-full flex items-center justify-end pr-2 flex-none min-w-0"
          style={{ flexBasis: `${Math.max(0, Math.min(100, occupiedPct))}%` }}
          title={`Occupied: ${formatMoney(occupied)} (${occupiedPct.toFixed(1)}%)`}
        >
          {occupiedPct > 12 && (
            <span className="text-xs font-medium text-white truncate">
              {occupiedPct.toFixed(0)}%
            </span>
          )}
        </div>
        <div
          className="bg-amber-400 h-full flex items-center justify-start pl-2 flex-none min-w-0"
          style={{ flexBasis: `${Math.max(0, Math.min(100, vacantPct))}%` }}
          title={`Vacant: ${formatMoney(vacant)} (${vacantPct.toFixed(1)}%)`}
        >
          {vacantPct > 12 && (
            <span className="text-xs font-medium text-white truncate">
              {vacantPct.toFixed(0)}%
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <CompositionLegend
          color="bg-emerald-500"
          label="Occupied bed cost"
          value={formatMoney(occupied)}
          sub={`${occupiedPct.toFixed(1)}% — rent on beds with a resident`}
        />
        <CompositionLegend
          color="bg-amber-400"
          label="Vacant bed cost"
          value={formatMoney(vacant)}
          sub={`${vacantPct.toFixed(1)}% — rent on empty beds${isMtd ? ' so far' : ''}`}
        />
      </div>
    </section>
  );
};

const CompositionLegend = ({ color, label, value, sub }) => (
  <div className="flex items-start gap-3">
    <div className={`w-3 h-3 ${color} rounded-sm mt-1 flex-shrink-0`} />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-gray-900 truncate">
        {label} · <span className="font-semibold">{value}</span>
      </p>
      <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Per-dorm breakdown — table inside a card, DS classes throughout.
// Sorted server-side by descending prorated cost.
// ---------------------------------------------------------------------------
const PerDormBreakdown = ({ dorms, monthDisplay }) => {
  if (!dorms.length) {
    return (
      <section className="bg-white rounded-2xl shadow-sm border">
        <header className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Dormitories</h3>
        </header>
        <div className="text-center py-12 px-6">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No dormitories</h3>
          <p className="text-sm text-gray-500">
            No dormitories have been configured in the Dormitory Portal yet.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-2xl shadow-sm border">
      <header className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Dormitories</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          Sorted by cost · {monthDisplay || 'this month'}
        </p>
      </header>

      {/* Horizontal scroll wrapper — this 5-column table is wider than a
          phone viewport; without it the table forces the whole card (and
          page) to overflow. Scrolling the table in isolation is the DS
          pattern for wide data tables on mobile. */}
      <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="table-header">
          <tr>
            <th scope="col" className="table-header-cell">Dormitory</th>
            <th scope="col" className="table-header-cell">Occupancy</th>
            <th scope="col" className="table-header-cell">Beds with a stay</th>
            <th scope="col" className="table-header-cell">Rate / bed</th>
            <th scope="col" className="table-header-cell text-right">Prorated cost</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {dorms.map((dorm) => {
            const occ = resolveOccupancy(dorm);
            return (
              <tr key={dorm.dorm_id} className="table-row">
                <td className="table-cell px-6 py-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                    <span className="truncate">{dorm.dorm_name}</span>
                    {dorm.is_company_owned && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 flex-shrink-0">
                        Company
                      </span>
                    )}
                  </div>
                  {dorm.address && (
                    <div className="text-xs text-gray-500 mt-0.5 truncate max-w-md">
                      {dorm.address}
                    </div>
                  )}
                </td>
                <td className="table-cell px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-900 w-28 flex-shrink-0 tabular-nums">
                      {formatLeasedOccupancy(dorm)}
                    </span>
                    <div className="w-24 bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full ${occupancyBarColor(occ.pct)}`}
                        style={{ width: `${Math.min(100, occ.pct)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-10 text-right tabular-nums">
                      {occ.pct.toFixed(0)}%
                    </span>
                  </div>
                </td>
                <td className="table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-700 tabular-nums">
                  {dorm.beds_with_stay_in_month ?? 0}
                </td>
                <td className="table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {dorm.cost_per_bed_monthly && Number(dorm.cost_per_bed_monthly) > 0
                    ? `${formatMoney(dorm.cost_per_bed_monthly)} / mo`
                    : '—'}
                </td>
                <td className="table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold tabular-nums">
                  {formatMoney(dorm.monthly_cost_prorated)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Loading skeleton — mirrors the layout above so the page doesn't jump
// when data arrives.
// ---------------------------------------------------------------------------
const OverviewSkeleton = () => (
  <div className="space-y-6">
    <div className="h-16 bg-blue-50 border border-blue-200 rounded-lg animate-pulse" />
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 xl:gap-6">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 animate-pulse">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0" />
            <div className="space-y-2 flex-1 min-w-0">
              <div className="h-6 bg-gray-200 rounded w-24" />
              <div className="h-4 bg-gray-200 rounded w-32" />
              <div className="h-3 bg-gray-200 rounded w-20" />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-4 animate-pulse">
      <div className="h-5 bg-gray-200 rounded w-48" />
      <div className="h-8 bg-gray-100 rounded-lg" />
    </div>
    <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-3 animate-pulse">
      <div className="h-5 bg-gray-200 rounded w-32" />
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-10 bg-gray-100 rounded" />
      ))}
    </div>
  </div>
);

export default OverviewTab;
